import React, { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { X, Mail, Sparkles, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import JunkFolderHint from "./JunkFolderHint";
import { track } from "../lib/gtag";

const STORAGE_KEY = "pg_email_capture_state"; // JSON: { status, ts }
const DISMISS_DAYS = 30;
const SUBSCRIBED_DAYS = 365;
const SHOW_AFTER_MS = 18_000;
const SHOW_AFTER_SCROLL_PCT = 50;

// Paths where the capture bar should NEVER appear
const EXCLUDED_PREFIXES = [
  "/admin",
  "/staff-portal-",
  "/checkout",
  "/cart",
  "/account",
  "/legal",
  "/order/",
  "/lp/",
];

function getState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function setState(status) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ status, ts: Date.now() }));
}

function isSuppressed() {
  const s = getState();
  if (!s) return false;
  const age = Date.now() - s.ts;
  if (s.status === "subscribed") return age < SUBSCRIBED_DAYS * 86400000;
  if (s.status === "dismissed") return age < DISMISS_DAYS * 86400000;
  return false;
}

export default function EmailCaptureBar() {
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const pathExcluded = EXCLUDED_PREFIXES.some((p) => location.pathname.startsWith(p));

  const maybeShow = useCallback(() => {
    if (pathExcluded) return;
    if (isSuppressed()) return;
    setVisible(true);
  }, [pathExcluded]);

  useEffect(() => {
    if (pathExcluded || isSuppressed()) {
      setVisible(false);
      return;
    }
    // Timer trigger
    const t = setTimeout(maybeShow, SHOW_AFTER_MS);
    // Scroll trigger
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const pct = max > 0 ? (window.scrollY / max) * 100 : 0;
      if (pct >= SHOW_AFTER_SCROLL_PCT) maybeShow();
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      clearTimeout(t);
      window.removeEventListener("scroll", onScroll);
    };
  }, [pathExcluded, maybeShow, location.pathname]);

  const dismiss = () => {
    setState("dismissed");
    setVisible(false);
  };

  const [showSuccess, setShowSuccess] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    setLoading(true);
    try {
      await api.post("/newsletter/subscribe", { email });
      track.signUpNewsletter();
      setState("subscribed");
      toast.success("Thanks! Your 10% code is on its way.");
      setShowSuccess(true);
      // Auto-hide the success panel after 10s
      setTimeout(() => setVisible(false), 10_000);
    } catch {
      toast.error("Something went wrong — please try again");
    } finally {
      setLoading(false);
    }
  };

  if (!visible) return null;

  // Success state: replace the form with a thank-you + junk-folder hint so
  // the user sees it for a few seconds before the bar auto-hides.
  if (showSuccess) {
    return (
      <div
        role="status"
        data-testid="email-capture-success"
        style={{
          position: "fixed",
          left: 16,
          right: 16,
          bottom: 16,
          maxWidth: 680,
          margin: "0 auto",
          zIndex: 70,
          background: "#0F172A",
          border: "1px solid rgba(166,255,0,0.5)",
          boxShadow: "0 10px 40px -12px rgba(166,255,0,0.3)",
          borderRadius: 6,
          padding: "14px 14px 14px 18px",
        }}
        className="flex flex-col gap-3"
      >
        <button
          type="button"
          onClick={() => setVisible(false)}
          aria-label="Dismiss"
          data-testid="email-capture-success-dismiss"
          style={{
            position: "absolute",
            top: 6,
            right: 6,
            background: "transparent",
            border: 0,
            padding: 6,
            cursor: "pointer",
            color: "#9CA8B8",
          }}
        >
          <X size={14} />
        </button>
        <div className="flex items-center gap-3">
          <CheckCircle2 size={20} className="text-[#A6FF00] shrink-0" />
          <div className="text-[#F7FAFC] text-sm font-semibold">
            Thanks — your 10% code is on its way.
          </div>
        </div>
        <JunkFolderHint email={email} what="your discount code" size="sm" />
      </div>
    );
  }

  return (
    <div
      role="dialog"
      aria-label="Join Peptigen Labs research list"
      data-testid="email-capture-bar"
      style={{
        position: "fixed",
        left: 16,
        right: 16,
        bottom: 16,
        maxWidth: 680,
        margin: "0 auto",
        zIndex: 70,
        background: "#0F172A",
        border: "1px solid rgba(0,194,214,0.4)",
        boxShadow: "0 10px 40px -12px rgba(0,194,214,0.35)",
        borderRadius: 6,
        padding: "14px 14px 14px 18px",
      }}
      className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3"
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <Sparkles size={18} className="text-[#A6FF00] shrink-0 hidden sm:block" />
        <div className="min-w-0">
          <div className="text-[#F7FAFC] text-sm font-semibold leading-tight">
            Get <span className="text-[#A6FF00]">10% off your first UK order</span>
          </div>
          <div className="text-[#9CA8B8] text-[11px] leading-snug mt-0.5 truncate">
            Restock alerts &middot; research notes &middot; no spam
          </div>
        </div>
      </div>
      <form onSubmit={submit} className="flex items-center gap-2 shrink-0 w-full sm:w-auto">
        <div className="relative flex-1 sm:flex-initial">
          <Mail size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9CA8B8]" />
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@lab.co.uk"
            data-testid="email-capture-input"
            style={{
              paddingLeft: 30,
              paddingRight: 10,
              paddingTop: 8,
              paddingBottom: 8,
              fontSize: 13,
              width: "100%",
              minWidth: 180,
              background: "#0B1220",
              color: "#F7FAFC",
              border: "1px solid #1E2E44",
              borderRadius: 3,
              outline: "none",
            }}
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          data-testid="email-capture-submit"
          style={{
            background: "#A6FF00",
            color: "#0B1220",
            fontWeight: 800,
            fontSize: 11,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            padding: "9px 14px",
            borderRadius: 3,
            border: 0,
            cursor: loading ? "wait" : "pointer",
            whiteSpace: "nowrap",
          }}
        >
          {loading ? "..." : "Get 10% off"}
        </button>
      </form>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        data-testid="email-capture-dismiss"
        style={{
          position: "absolute",
          top: 6,
          right: 6,
          background: "transparent",
          border: 0,
          padding: 6,
          cursor: "pointer",
          color: "#9CA8B8",
        }}
      >
        <X size={14} />
      </button>
    </div>
  );
}
